/**
 * Shared post-commit outcome-bucket result presentation
 * (action-breakdown-remaining-dialogs task 4.1). Consolidates the lock-reservations
 * dialog's and the release-rejected-units dialog's near-identical private bucket
 * views (`LockOutcomeBucket`/`LockOutcomeEntryRow`/`lockOutcomeFailedDetail` in
 * lock-reservations-dialog.tsx, `ReleaseOutcomeBucket` in unit-decisions-section.tsx)
 * into one shared module, following the same outcome-vocabulary conventions the
 * preceding change's `OutcomeBreakdownTable`/`outcome-breakdown-columns.tsx`
 * established for the PRE-commit breakdown: every entry is identified via
 * `MediaUnitLink` (never a raw mediaUnitId/reservationId), and an unresolvable
 * reason is explained in business language with a generic fallback, never a raw
 * error identifier.
 *
 * Deliberately distinct from `OutcomeBreakdownTable`: that module renders a single
 * flat `CampaignOutcomeItem[]` (the shared action-breakdown vocabulary) joined
 * against the offer's own unit-table columns, for a PRE-commit preview. This module
 * renders POST-commit action results, which the lock/release-rejected-units
 * endpoints already bucket into separate arrays with their own outcome-specific
 * shapes (`LockReservationsResponse.locked/queued/failed`,
 * `ReleaseRejectedUnitsResponse.released/removed/failed`) rather than the shared
 * `CampaignOutcomeItem` vocabulary — so entries render per bucket via
 * `OutcomeResultBucket`/`OutcomeResultEntryRow`, not joined into one table.
 *
 * The window (`dateFrom`/`dateTo`) is optional per entry: a lock outcome always
 * carries it, a release-action outcome (`ReleasedUnit`/`RemovedUnit`/
 * `FailedReleaseUnit`) never does — `MediaUnitLink` alone still identifies the
 * nośnik correctly, with no date-range line shown for those entries.
 */
import * as React from 'react';
import { CollapsibleSection } from '@ooh/ui/components/collapsible-section';
import { MediaUnitLink } from '@ooh/ui/components/media-unit-link';
import { Badge, type BadgeProps } from '@ooh/ui/components/badge';
import { Button } from '@ooh/ui/components/button';
import { ApiErrorCode } from '@ooh/contracts';
import { formatDisplayDateRange } from '@ooh/ui/lib/date-format';

/** i18next's typed `TFunction<'offers'>` only accepts key literals it can statically
 * verify — a key shared across dialogs via this module needs the untyped call
 * signature instead, same cast `outcome-breakdown-columns.tsx`'s `LooseT` applies
 * for the identical reason. */
export type LooseT = (key: string, options?: Record<string, unknown>) => string;

/** Shared wrapper styling for a popover's post-commit result panel — used by both
 * `unified-select-popover.tsx`'s Rezerwacja result and `ReleaseSelectedResultView`'s
 * Zwolnij (mode='selected') result, so the two stay visually in sync. */
export const RESULT_PANEL_WRAPPER_CLASS =
  'mb-2.5 space-y-1.5 rounded-md border border-border/60 p-2';

/** Display metadata resolved per mediaUnitId, e.g. from a version's unit-decisions
 * query — the same shape `LockReservationsDialog` already resolves via
 * `useUnitDecisions` for its own result rows. */
export interface OutcomeResultUnitMeta {
  grwNumber: string | null;
  address: string | null;
  city: string | null;
}
export type OutcomeResultUnitLookup = Map<number, OutcomeResultUnitMeta>;

export interface OutcomeResultEntry {
  mediaUnitId: number;
  dateFrom?: Date;
  dateTo?: Date;
}

function entryKey(entry: OutcomeResultEntry): string {
  return entry.dateFrom != null && entry.dateTo != null
    ? `${entry.mediaUnitId}-${entry.dateFrom.toISOString()}-${entry.dateTo.toISOString()}`
    : `${entry.mediaUnitId}`;
}

/** Distinct-nośnik count for one outcome group — a nośnik booked for several
 * windows is still one nośnik (action-breakdown / "Counts are per nośnik, not per
 * window"). Shared by every breakdown view so the counting rule can't drift
 * between the lock, release-rejected, withdrawal, and Zwolnij dialogs. */
export function distinctUnitCount(entries: { mediaUnitId: number }[]): number {
  return new Set(entries.map((entry) => entry.mediaUnitId)).size;
}

export interface BreakdownGroupChipProps {
  testId: string;
  variant: BadgeProps['variant'];
  label: string;
  count: number;
}

/** One outcome group's summary chip (badge + distinct-nośnik count) atop a
 * pre-commit breakdown — shared so a future visual tweak (spacing, badge
 * placement, count formatting) applied to one breakdown dialog can't
 * silently diverge from the others. */
export function BreakdownGroupChip({ testId, variant, label, count }: BreakdownGroupChipProps) {
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm"
    >
      <Badge variant={variant}>{label}</Badge>
      <span className="font-semibold tabular-nums">{count}</span>
    </div>
  );
}

export interface BreakdownPreviewStateProps {
  preview: {
    isLoading: boolean;
    isError: boolean;
    refetch: () => void;
  };
  /** Prefix for this state's `data-testid` (`-loading`/`-error`/`-retry`/
   * `-empty`), kept dialog-specific so the lock/withdraw/release dialogs'
   * existing, test-pinned ids (`lock-breakdown-*`, `withdraw-breakdown-*`,
   * `release-breakdown-*`) survive this extraction unchanged. */
  testIdPrefix: string;
  loadingLabel: string;
  errorLabel: string;
  retryLabel: string;
  /** Whether the resolved preview has nothing for the caller to act on
   * (action-breakdown / "An action with nothing to do says so"). Each
   * caller defines "nothing to do" its own way (an empty response for
   * withdrawal/release, "every nośnik already held" for the lock preview). */
  isEmpty: boolean;
  emptyLabel: string;
  children: React.ReactNode;
}

/**
 * Shared loading/error+retry/nothing-to-do scaffolding around a pre-commit
 * breakdown's content — extracted out of three independent hand-rolled
 * copies (LockBreakdownView, WithdrawalBreakdownView, ReleaseBreakdownView),
 * so those three breakdown views look and behave identically for these three
 * states. (The availability grid's Zwolnij breakdown,
 * unified-select-release-breakdown-view.tsx, still has its own copy — out of
 * scope for this extraction.) Renders `children` only once the preview has
 * resolved, is not in error, and is not empty.
 */
export function BreakdownPreviewState({
  preview,
  testIdPrefix,
  loadingLabel,
  errorLabel,
  retryLabel,
  isEmpty,
  emptyLabel,
  children,
}: BreakdownPreviewStateProps) {
  if (preview.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-loading`}>
        {loadingLabel}
      </p>
    );
  }

  if (preview.isError) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-error`}>
        <p className="text-sm text-destructive">{errorLabel}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => preview.refetch()}
          data-testid={`${testIdPrefix}-retry`}
        >
          {retryLabel}
        </Button>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
        {emptyLabel}
      </p>
    );
  }

  return <>{children}</>;
}

export interface OutcomeResultBucketProps<T extends OutcomeResultEntry> {
  trigger: React.ReactNode;
  entries: T[];
  emptyLabel: string;
  /**
   * Prefix for each entry's `data-testid`, kept dialog-specific so the lock
   * dialog's existing, test-pinned `lock-outcome-entry-*` ids survive this
   * extraction unchanged (lock-reservations-dialog.test.tsx and
   * lock-reservations-dialog-failed-detail.test.tsx assert this exact format).
   */
  testIdPrefix: string;
  renderEntry: (entry: T) => React.ReactNode;
}

/** One outcome's collapsible entry list — the generic bucket shell both dialogs'
 * result views render every outcome group through. */
export function OutcomeResultBucket<T extends OutcomeResultEntry>({
  trigger,
  entries,
  emptyLabel,
  testIdPrefix,
  renderEntry,
}: OutcomeResultBucketProps<T>) {
  return (
    <CollapsibleSection trigger={trigger}>
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground px-4 py-3">{emptyLabel}</p>
      ) : (
        <ul className="divide-y divide-border/50">
          {entries.map((entry) => (
            <li
              key={entryKey(entry)}
              className="px-4 py-2"
              data-testid={`${testIdPrefix}-${entryKey(entry)}`}
            >
              {renderEntry(entry)}
            </li>
          ))}
        </ul>
      )}
    </CollapsibleSection>
  );
}

export interface OutcomeResultEntryRowProps {
  unit: OutcomeResultUnitMeta | undefined;
  /**
   * Window is optional — absent entirely for release-action outcomes, which
   * carry no dateFrom/dateTo. Accepts a bare ISO date string (e.g. a
   * `DateRange.from`/`.to`) as well as a `Date`, since `formatDisplayDateRange`
   * below already handles both — passing a string through directly avoids a
   * caller having to `new Date(isoDateOnlyString)`, which loses the date-only
   * string's timezone-safe parsing (see `date-format.ts`'s `toDate` doc comment).
   */
  dateFrom?: Date | string;
  dateTo?: Date | string;
  detail?: string;
  onActivate?: () => void;
  unknownUnitLabel: string;
}

/**
 * One outcome row: a `MediaUnitLink` identified by the canonical GRW display ID
 * (address + city as its subtitle, when both are known), plus the date range
 * (when the entry carries one) and an optional detail line as secondary text.
 * Never renders the raw mediaUnitId or reservationId — see CU-86cb4wkxp.
 *
 * A caller without a full metadata source for its lookup (e.g. the availability
 * grid's batch-release breakdown, which has no `useUnitDecisions`-equivalent
 * query for an arbitrary cross-unit selection — see `unified-select-popover.tsx`'s
 * `buildReleaseBreakdownLookup`) may resolve only a `grwNumber`, leaving
 * `address`/`city` both null. Rendering `${null}, ${null}` as the subtitle would
 * show the literal string "null, null" — CU-86cbgpguu QA finding — so the
 * subtitle is omitted whenever either half is missing, rather than fixed at each
 * such call site.
 */
function unitSubtitle(unit: OutcomeResultUnitMeta | undefined): string | undefined {
  if (unit?.address == null || unit.city == null) return undefined;
  return `${unit.address}, ${unit.city}`;
}

export function OutcomeResultEntryRow({
  unit,
  dateFrom,
  dateTo,
  detail,
  onActivate,
  unknownUnitLabel,
}: OutcomeResultEntryRowProps) {
  const hasWindow = dateFrom != null && dateTo != null;
  const hasSecondary = hasWindow || detail != null;

  return (
    <div className="flex items-center justify-between gap-3">
      <MediaUnitLink
        className="min-w-0 flex-1"
        identifier={unit?.grwNumber ?? unknownUnitLabel}
        subtitle={unitSubtitle(unit)}
        onActivate={onActivate}
      />
      {hasSecondary ? (
        // Fixed max-w caps the detail column so a long localized explanation (e.g.
        // the MIN_WINDOW_VIOLATION business message) wraps onto multiple lines
        // instead of forcing MediaUnitLink's flex-1/min-w-0 sibling to zero width —
        // CU-86cb5z48p found the unit identifier disappearing entirely behind the
        // old shrink-0-only layout once the detail text grew past a short
        // "Błąd: CODE"/"Pozycja w kolejce: N" line.
        <div className="max-w-[220px] shrink-0 text-right text-xs leading-tight text-muted-foreground">
          {hasWindow ? <p>{formatDisplayDateRange(dateFrom, dateTo)}</p> : null}
          {detail != null ? <p className="whitespace-normal">{detail}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Business-readable reason for a bucket entry the action could not resolve —
 * generalized from the lock dialog's own `lockOutcomeFailedDetail` (CU-86cb5z48p),
 * reused verbatim by the release-rejected dialog's own `failed` bucket
 * (`FailedReleaseUnit.errorCode` carries no minWindowDays/selectedDays of its own,
 * so it always takes the generic-fallback/raw-code branches below). MIN_WINDOW_
 * VIOLATION gets a dedicated message with the actual required-vs-selected day
 * counts, reusing the `reservations` namespace's
 * `toasts.batchReserveConflictMinWindowViolationExact` key verbatim (cross-
 * namespace `t()` lookup, same pattern as `outcome-breakdown-columns.tsx`'s
 * `describeOutcomeReason`) instead of duplicating that wording into a second
 * locale key. RESERVATION_CONFLICT (a nośnik held by another party) and
 * AVAILABILITY_WINDOW_BLOCKED (a period the owner marked unavailable) are the
 * other two reasons the lock preview actually emits (offer-versions.service.ts's
 * `previewLockOverlap`/`previewFreshLockPair`), so both get their own dedicated
 * business-language message too. Any other errorCode — including one this build
 * has no specific wording for — falls back to a generic explanation, never the
 * raw identifier (action-breakdown / "An unrecognised failure still avoids
 * leaking an identifier"; CU-86cbgpguu QA finding: RESERVATION_CONFLICT was
 * previously leaking as "Błąd: RESERVATION_CONFLICT").
 */
export function outcomeFailedDetail(
  entry: { errorCode: string; minWindowDays?: number; selectedDays?: number },
  t: LooseT,
): string {
  if (entry.errorCode === ApiErrorCode.MIN_WINDOW_VIOLATION) {
    if (entry.minWindowDays != null && entry.selectedDays != null) {
      // `count` drives i18next's _one/_few/_many/_other plural suffix on
      // minWindowDays (e.g. "1 dzień" vs "30 dni").
      return t('reservations:toasts.batchReserveConflictMinWindowViolationExact', {
        minWindowDays: entry.minWindowDays,
        selectedDays: entry.selectedDays,
        count: entry.minWindowDays,
      });
    }
    return t('workspace.versions.lock.result.entryFailedMinWindowViolationGeneric');
  }
  if (entry.errorCode === ApiErrorCode.RESERVATION_CONFLICT) {
    return t('workspace.versions.lock.result.entryFailedReservationConflict');
  }
  if (entry.errorCode === ApiErrorCode.AVAILABILITY_WINDOW_BLOCKED) {
    return t('workspace.versions.lock.result.entryFailedAvailabilityWindowBlocked');
  }
  return t('workspace.versions.lock.result.entryFailedGeneric');
}
